/**
 * Backend AUM-snapshots API — typed TanStack Query hook for the hero growth chart.
 *
 * GET /v1/trusts/{trustId}/aum-snapshots[?limit=N]
 * Default limit is 12 months; max 60. Sorted newest month first.
 *
 * Snapshots are materialised daily by the aum-snapshot-aggregator timer
 * on the audit-changefeed FA. First nightly run populates the current
 * month; over time the chart fills in (each subsequent monthly tick adds
 * another point).
 */

import { useQuery } from '@tanstack/react-query';
import { api } from './client';

export type AumSnapshotSource = 'trust-doc-total-value' | 'portfolio-aggregate';

export interface AumSnapshotView {
  id: string;
  tenantId: string;
  trustId: string;
  /** ISO month, e.g. '2026-05'. */
  month: string;
  valueZar: number;
  capturedAt: string;
  source: AumSnapshotSource;
  createdAt: string;
  updatedAt: string;
}

export interface AumSnapshotsListResponse {
  aumSnapshots: AumSnapshotView[];
  correlationId: string;
}

export interface UseAumSnapshotsOptions {
  limit?: number;
}

/** Key + fetcher shared by the hook AND the auth-time dashboard prefetch
 *  (`prefetch-dashboard.ts`) — one definition, so the prefetched cache entry
 *  is guaranteed to be the exact one the hero chart reads. */
export function aumSnapshotsQueryKey(trustId: string | undefined, limit?: number) {
  return ['aum-snapshots', 'list', trustId ?? null, limit ?? null] as const;
}

export function fetchAumSnapshots(
  trustId: string,
  limit?: number,
  signal?: AbortSignal,
): Promise<AumSnapshotsListResponse> {
  const params = new URLSearchParams();
  if (limit) params.set('limit', String(limit));
  const q = params.toString();
  const url = q
    ? `/api/v1/trusts/${encodeURIComponent(trustId)}/aum-snapshots?${q}`
    : `/api/v1/trusts/${encodeURIComponent(trustId)}/aum-snapshots`;
  return api<AumSnapshotsListResponse>(url, { signal });
}

export function useAumSnapshots(
  trustId: string | undefined,
  options: UseAumSnapshotsOptions = {},
) {
  return useQuery<AumSnapshotsListResponse>({
    queryKey: aumSnapshotsQueryKey(trustId, options.limit),
    queryFn: ({ signal }) => fetchAumSnapshots(trustId!, options.limit, signal),
    enabled: !!trustId,
  });
}
